import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

type MiddleCategoryType = 'reading' | 'writing' | 'vocabulary' | 'grammar' | 'listening';
type HighCategoryType = 'regular' | 'suneung';

// 교재 데이터 타입
interface TextbookData {
  grade: string;
  image: string;
  description: string;
}

interface ClassData {
  className: string;
  classColor: string;
  textbooks: TextbookData[];
}

// 단어 교재 데이터 (8단계)
interface VocabularyStage {
  stage: number;
  image: string;
  name: string;
}

const vocabularyStages = [
  { stage: 1, name: 'Word Master\n중등 실력', image: 'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/f6b38a20748ae9e6ab33642e564c9ab4.png' },
  { stage: 2, name: 'Word Master\n중등 고난도', image: 'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/b11de2d3a84a94c2db1dd99695735a57.png' },
  { stage: 3, name: 'Word Master\n고등 베이직', image: 'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/17272476e454aaac3416dea63b83dfb2.png' },
  { stage: 4, name: '커넥티드 보카', image: 'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/ee39917dea620c413702400127acaa62.png' },
  { stage: 5, name: 'Word Master\n수능 2000', image: 'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/3483b27d68cd4086b0a46111864d706d.png' },
  { stage: 6, name: 'EBS VOCA\n어원', image: 'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/c782da0c6414feba7230aadc78a36e98.png' },
  { stage: 7, name: '모의고사\n기출단어', image: 'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/265bb4e4f8dbf6cf11e57ba5207200b0.jpeg' },
  { stage: 8, name: 'Word Master\n하이퍼', image: 'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/d6dd48c9655538c078adf96bbd5bc081.png' },
];

export default function EnglishCurriculumPage() {
  const [courseType, setCourseType] = useState<'middle' | 'high'>('middle');
  const [middleCategory, setMiddleCategory] = useState<MiddleCategoryType>('reading');
  const [highCategory, setHighCategory] = useState<HighCategoryType>('regular');

  // Set page title and meta tags
  useEffect(() => {
    document.title = '미래인재학원 영어 커리큘럼';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '미래인재학원의 영어 커리큘럼을 확인하세요. 문법, 독해, 듣기, 어휘까지 체계적인 영어 교육으로 입시를 완벽하게 준비합니다.');
    }
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', '미래인재학원 영어 커리큘럼');
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', '미래인재학원의 영어 커리큘럼을 확인하세요. 문법, 독해, 듣기, 어휘까지 체계적인 영어 교육으로 입시를 완벽하게 준비합니다.');
    }

    // Schema.org Course structured data
    const existingSchema = document.getElementById('schema-english-course');
    if (!existingSchema) {
      const script = document.createElement('script');
      script.id = 'schema-english-course';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Course",
        "name": "미래인재학원 영어 커리큘럼",
        "description": "독해, 영작, 문법, 단어, 듣기(16레벨) 영역별 체계적 수업. S반, A반, B/C반 레벨별 맞춤 수업과 자체 제작 교재로 입시를 완벽하게 준비합니다.",
        "provider": {
          "@type": "EducationalOrganization",
          "name": "미래인재학원",
          "url": "https://eliteoffuture1.com/"
        },
        "educationalLevel": ["중학교", "고등학교"],
        "about": {
          "@type": "Thing",
          "name": "영어"
        },
        "hasCourseInstance": [
          {
            "@type": "CourseInstance",
            "name": "중등 영어 과정",
            "description": "독해/영작, 문법, 단어(8단계), 듣기(16레벨) 영역별 체계적 수업. 모의고사 지문 기반 자체 교재 사용.",
            "courseMode": "onsite",
            "location": {
              "@type": "Place",
              "name": "미래인재학원 (지행, 옥정, 민락 캠퍼스)"
            }
          },
          {
            "@type": "CourseInstance",
            "name": "고등 영어 내신 과정",
            "description": "학교별 맞춤 수업, 철저한 수행평가 관리, 4주간 시험 준비, 동형 모의고사, 매주 주간고사, 무한 영상 보강.",
            "courseMode": "onsite"
          },
          {
            "@type": "CourseInstance",
            "name": "고등 영어 수능 과정",
            "description": "수능 1등급 달성 시 50% 환급, 매주 주간 모의고사, 방학 킬러문항 특강, EBS 연계 학습.",
            "courseMode": "onsite"
          }
        ]
      });
      document.head.appendChild(script);
    }

    return () => {
      const schemaEl = document.getElementById('schema-english-course');
      if (schemaEl) schemaEl.remove();
    };
  }, []);

  const middleCategories = [
    { id: 'reading', name: '독해 / 영작' },
    { id: 'grammar', name: '문법' },
    { id: 'vocabulary', name: '단어' },
    { id: 'listening', name: '듣기' },
  ];

  const highCategories = [
    { id: 'regular' as HighCategoryType, name: '내신수업' },
    { id: 'suneung' as HighCategoryType, name: '수능수업' },
  ];

  // 독해 교재 데이터
  const readingData: ClassData[] = [
    {
      className: 'S반',
      classColor: 'from-emerald-600 to-green-700',
      textbooks: [
        {
          grade: '중1',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/67bb7ec91466c87a1d5b39fabf96af83.png',
          description: '고1 모의고사 지문을 편집한 자체 교재 (총 11권)',
        },
        {
          grade: '중2',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/7d06df073c824f183f0cb77c6b66e046.png',
          description: '고2 모의고사 지문을 편집한 자체 교재 (총 10권)',
        },
        {
          grade: '중3',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/1207cb48c99cffb1d2f7afa4fdfe6f18.png',
          description: '고3 모의고사 지문을 편집한 자체 교재 (총 10권)',
        },
      ],
    },
    {
      className: 'A반',
      classColor: 'from-teal-500 to-emerald-500',
      textbooks: [
        {
          grade: '중1',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/86931869aa865476d4e0bb740ad23a10.png',
          description: '고1 모의고사 지문을 편집한 자체 교재 (총 11권)',
        },
        {
          grade: '중2',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/9971f0929465620530035e11cd0ca476.png',
          description: '고1,2 모의고사 지문을 편집한 자체 교재 (총 10권)',
        },
        {
          grade: '중3',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/eeb4c4fc7947c2c9b283f0198e2e761a.png',
          description: '고2 모의고사 지문을 편집한 자체 교재 (총 10권)',
        },
      ],
    },
    {
      className: 'B/C반',
      classColor: 'from-green-400 to-teal-400',
      textbooks: [
        {
          grade: '중1',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/3d9429e6c5d108a703ae8fc23e432af9.png',
          description: '중2 교과서 수준 지문을 편집한 자체 교재(총 8권)',
        },
        {
          grade: '중2',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/2319005406b9ee106d50670509f4f6cb.png',
          description: '중3 교과서 수준 지문을 편집한 자체 교재(총 8권)',
        },
        {
          grade: '중3',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/5b5f7255619bee7377fa65db1ce6ad3d.png',
          description: '고1 모의고사 지문을 편집한 자체 교재 (총 8권)',
        },
      ],
    },
  ];

  // 영작 교재 데이터
  const writingData: ClassData[] = [
    {
      className: 'S반',
      classColor: 'from-emerald-600 to-green-700',
      textbooks: [
        {
          grade: '중1',
          image:
            'https://public.readdy.ai/ai/img_res/ef9f509a340526ce35d61a991d402b59.jpg',
          description: '중2 수준 문장 구성 및 영작',
        },
        {
          grade: '중2',
          image:
            'https://readdy.ai/api/search-image?query=Professional%20English%20writing%20composition%20textbook%20cover%20design%20for%20Korean%20middle%20school%20grade%202%20advanced%20level%20with%20pen%20and%20paper%20icons%20modern%20clean%20layout%20on%20warm%20cream%20background%20A4%20portrait%20format&width=210&height=297&seq=writing-s-2&orientation=portrait',
          description: '중3 수준 문장 구성 및 영작',
        },
        {
          grade: '중3',
          image:
            'https://readdy.ai/api/search-image?query=Professional%20English%20writing%20composition%20textbook%20cover%20design%20for%20Korean%20middle%20school%20grade%203%20advanced%20level%20with%20pen%20and%20paper%20icons%20modern%20clean%20layout%20on%20warm%20ivory%20background%20A4%20portrait%20format&width=210&height=297&seq=writing-s-3&orientation=portrait',
          description: '고1 수준 문장 구성 및 영작',
        },
      ],
    },
    {
      className: 'A반',
      classColor: 'from-teal-500 to-emerald-500',
      textbooks: [
        {
          grade: '중1',
          image:
            'https://readdy.ai/api/search-image?query=Professional%20English%20writing%20textbook%20cover%20design%20for%20Korean%20middle%20school%20grade%201%20intermediate%20level%20with%20writing%20icons%20modern%20layout%20on%20soft%20sage%20green%20background%20A4%20portrait%20format&width=210&height=297&seq=writing-a-1&orientation=portrait',
          description: '중1 심화 수준 영작 훈련',
        },
        {
          grade: '중2',
          image:
            'https://readdy.ai/api/search-image?query=Professional%20English%20writing%20textbook%20cover%20design%20for%20Korean%20middle%20school%20grade%202%20intermediate%20level%20with%20writing%20icons%20modern%20layout%20on%20soft%20rose%20background%20A4%20portrait%20format&width=210&height=297&seq=writing-a-2&orientation=portrait',
          description: '중2 심화 수준 영작 훈련',
        },
        {
          grade: '중3',
          image:
            'https://readdy.ai/api/search-image?query=Professional%20English%20writing%20textbook%20cover%20design%20for%20Korean%20middle%20school%20grade%203%20intermediate%20level%20with%20writing%20icons%20modern%20layout%20on%20soft%20sand%20background%20A4%20portrait%20format&width=210&height=297&seq=writing-a-3&orientation=portrait',
          description: '중3 심화 수준 영작 훈련',
        },
      ],
    },
    {
      className: 'B/C반',
      classColor: 'from-green-400 to-teal-400',
      textbooks: [
        {
          grade: '중1',
          image:
            'https://readdy.ai/api/search-image?query=Professional%20English%20writing%20textbook%20cover%20design%20for%20Korean%20middle%20school%20grade%201%20basic%20level%20with%20simple%20writing%20icons%20friendly%20layout%20on%20light%20aqua%20background%20A4%20portrait%20format&width=210&height=297&seq=writing-bc-1&orientation=portrait',
          description: '중1 기본 수준 영작 훈련',
        },
        {
          grade: '중2',
          image:
            'https://readdy.ai/api/search-image?query=Professional%20English%20writing%20textbook%20cover%20design%20for%20Korean%20middle%20school%20grade%202%20basic%20level%20with%20simple%20writing%20icons%20friendly%20layout%20on%20light%20pink%20background%20A4%20portrait%20format&width=210&height=297&seq=writing-bc-2&orientation=portrait',
          description: '중2 기본 수준 영작 훈련',
        },
        {
          grade: '중3',
          image:
            'https://readdy.ai/api/search-image?query=Professional%20English%20writing%20textbook%20cover%20design%20for%20Korean%20middle%20school%20grade%203%20basic%20level%20with%20simple%20writing%20icons%20friendly%20layout%20on%20light%20lilac%20background%20A4%20portrait%20format&width=210&height=297&seq=writing-bc-3&orientation=portrait',
          description: '중3 기본 수준 영작 훈련',
        },
      ],
    },
  ];

  // 문법 교재 데이터
  const grammarData: ClassData[] = [
    {
      className: 'S반',
      classColor: 'from-emerald-600 to-green-700',
      textbooks: [
        {
          grade: '중1',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/b3e991f0495bcb4504ac00b8eb4e0a7e.png',
          description: '중등 과정에 필요한 문법 자체 교재(총 11권)',
        },
        {
          grade: '중2',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/52907fae0f9dfaffeb422dbd6b53c217.png',
          description: '중등 과정에 필요한 문법 정리 자제 교재(총 6권) GRAMMAR KEYWORDS.1(수능 어법 교재)',
        },
        {
          grade: '중3',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/d60c2d479668e79f3ba22c3b8b0857e6.png',
          description: 'GRAMMAR KEYWORDS.2(수능 어법 교재)',
        },
      ],
    },
    {
      className: 'A반',
      classColor: 'from-teal-500 to-emerald-500',
      textbooks: [
        {
          grade: '중1',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/3f5c76a64730b144072cde1f97eee112.png',
          description: '중등 과정에 필요한 문법 차제 교재(총 11권)',
        },
        {
          grade: '중2',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/01b9ee7393ca4ce460433b3f6c1f6b60.png',
          description: '중등 과정에 필요한 문법 정리 자제 교재(총 6권) GRAMMAR KEYWORDS.1(수능 어법 교재)',
        },
        {
          grade: '중3',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/6c75e87c9b6b18d4b222dccbe1996dbd.png',
          description: 'GRAMMAR KEYWORDS.2 (수능 어법 교재)',
        },
      ],
    },
    {
      className: 'B/C반',
      classColor: 'from-green-400 to-teal-400',
      textbooks: [
        {
          grade: '중1',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/09f2ff40643aefa4c4d9d98194fa7a4d.png',
          description: '중1 기본 수준에 필요한 문법 교재(총 11권)',
        },
        {
          grade: '중2',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/0736cac3e27ae0ee48cadea106a46aed.png',
          description: '중2 기본 수준에 필요한 문법 교재(총 11권)',
        },
        {
          grade: '중3',
          image:
            'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/51c20798a0b39bb5c192b207496aa48f.png',
          description: 'GRAMMAR KEYWORD.1(수능 어법 교재)',
        },
      ],
    },
  ];

  // 교재 섹션 렌더링 컴포넌트
  const renderTextbookSection = (data: ClassData[]) => (
    <div className="space-y-16">
      {data.map((classInfo) => (
        <div key={classInfo.className} className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* 반 헤더 */}
          <div className={`bg-gradient-to-r ${classInfo.classColor} px-8 py-5`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
              <h3 className="text-2xl font-bold text-white">{classInfo.className}</h3>
              {classInfo.className === 'S반' && (
                <p className="text-white/90 text-sm sm:text-2xl font-medium leading-snug">
                  <span className="sm:hidden">중학교 때, 수능 영어 1등급 완성을<br />목표로 합니다!</span>
                  <span className="hidden sm:inline">중학교 때, 수능 영어 1등급 완성을 목표로 합니다!</span>
                </p>
              )}
              {classInfo.className === 'A반' && (
                <p className="text-white/90 text-sm sm:text-2xl font-medium leading-snug">고등학교 내신 1등급을 목표로 합니다!</p>
              )}
              {classInfo.className === 'B/C반' && (
                <p className="text-white/90 text-sm sm:text-2xl font-medium leading-snug">학교 시험 100점을 목표로 합니다!</p>
              )}
            </div>
          </div>

          {/* 교재 그리드 */}
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {classInfo.textbooks.map((textbook) => (
                <div key={textbook.grade} className="flex flex-col items-center">
                  {/* 학년 라벨 */}
                  <div className="mb-4">
                    <span
                      className={`inline-block px-4 py-2 rounded-full text-white font-bold text-sm bg-gradient-to-r ${classInfo.classColor}`}
                    >
                      {textbook.grade}
                    </span>
                  </div>

                  {/* 교재 이미지 (A4 비율) */}
                  <div className="w-48 h-64 bg-gray-100 rounded-lg shadow-md overflow-hidden mb-4 border border-gray-200">
                    <img
                      src={textbook.image.replace('https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/e3439cb5f16458bd42b93ca4bdb36af0.png', 'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/67bb7ec91466c87a1d5b39fabf96af83.png')}
                      alt={`${classInfo.className} ${textbook.grade} 교재`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src =
                          'https://via.placeholder.com/192x256?text=Image+Not+Found';
                      }}
                    />
                  </div>

                  {/* 교재 설명 */}
                  <p className="text-center text-gray-600 text-sm font-medium">
                    <span className="hidden sm:inline">{textbook.description}</span>
                    <span className="sm:hidden">
                      {textbook.description.includes('(총') 
                        ? <>
                            {textbook.description.split('(총')[0].trim()}<br />
                            (총{textbook.description.split('(총')[1]}
                          </>
                        : textbook.description
                      }
                    </span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // 단어 섹션 렌더링
  const renderVocabularySection = () => (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* 헤더 */}
      <div className="bg-gradient-to-r from-emerald-600 to-green-700 px-8 py-6">
        <h3 className="text-2xl font-bold text-white text-center">단어는 학생 개개인마다 각기 다른 단계별로 진행합니다!</h3>
      </div>

      {/* 8단계 교재 그리드 */}
      <div className="p-8">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6">
          {vocabularyStages.map((item) => (
            <div key={item.stage} className="flex flex-col items-center">
              {/* 단계 라벨 */}
              <div className="mb-3">
                <span className="inline-block px-3 py-1.5 rounded-full text-white font-bold text-sm bg-gradient-to-r from-emerald-500 to-green-600">
                  {item.stage}단계
                </span>
              </div>

              {/* 교재 이미지 */}
              <div className="w-24 h-32 bg-gray-100 rounded-lg shadow-md overflow-hidden mb-3 border border-gray-200 flex items-center justify-center">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={`${item.stage}단계 교재`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center text-gray-400">
                    <i className="ri-image-add-line text-2xl mb-1"></i>
                    <span className="text-xs">이미지</span>
                  </div>
                )}
              </div>

              {/* 교재 이름 */}
              <p className="text-center text-gray-700 text-xs font-medium leading-tight whitespace-pre-line">
                {item.name}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // 공란 섹션 렌더링
  const renderEmptySection = (title: string) => (
    <div className="text-center py-20">
      <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
        <i className="ri-file-list-3-line text-3xl text-gray-400"></i>
      </div>
      <h2 className="text-3xl font-bold text-gray-900 mb-4">{title}</h2>
      <p className="text-lg text-gray-500">콘텐츠 준비 중입니다</p>
    </div>
  );

  // 듣기 섹션 렌더링
  const renderListeningSection = () => (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* 헤더 */}
      <div className="bg-gradient-to-r from-emerald-600 to-green-700 px-8 py-6">
        <h3 className="text-2xl font-bold text-white text-center">듣기는 학생 개개인마다 각기 다른 레벨별로 진행합니다!</h3>
      </div>

      {/* 듣기 레벨 그리드 */}
      <div className="p-8">
        <div className="space-y-8">
          {/* 중1 수준 */}
          <div className="flex flex-col items-center">
            <div className="mb-4">
              <span className="inline-block px-5 py-2 rounded-full text-white font-bold text-base bg-gradient-to-r from-emerald-500 to-green-600">
                중1 수준 듣기
              </span>
            </div>
            <div className="flex gap-4">
              {[1, 2, 3].map((level) => (
                <div key={level} className="flex flex-col items-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl shadow-md flex items-center justify-center border-2 border-emerald-300">
                    <span className="text-emerald-700 font-bold text-lg">레벨{level}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 중2 수준 */}
          <div className="flex flex-col items-center">
            <div className="mb-4">
              <span className="inline-block px-5 py-2 rounded-full text-white font-bold text-base bg-gradient-to-r from-emerald-500 to-green-600">
                중2 수준 듣기
              </span>
            </div>
            <div className="flex gap-4">
              {[4, 5, 6].map((level) => (
                <div key={level} className="flex flex-col items-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl shadow-md flex items-center justify-center border-2 border-emerald-300">
                    <span className="text-emerald-700 font-bold text-lg">레벨{level}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 중3 수준 */}
          <div className="flex flex-col items-center">
            <div className="mb-4">
              <span className="inline-block px-5 py-2 rounded-full text-white font-bold text-base bg-gradient-to-r from-emerald-500 to-green-600">
                중3 수준 듣기
              </span>
            </div>
            <div className="flex gap-4">
              {[7, 8, 9].map((level) => (
                <div key={level} className="flex flex-col items-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl shadow-md flex items-center justify-center border-2 border-emerald-300">
                    <span className="text-emerald-700 font-bold text-lg">레벨{level}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 고1 수준 */}
          <div className="flex flex-col items-center">
            <div className="mb-4">
              <span className="inline-block px-5 py-2 rounded-full text-white font-bold text-base bg-gradient-to-r from-emerald-500 to-green-600">
                고1 수준 듣기
              </span>
            </div>
            <div className="flex gap-4">
              {[10, 11, 12].map((level) => (
                <div key={level} className="flex flex-col items-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl shadow-md flex items-center justify-center border-2 border-emerald-300">
                    <span className="text-emerald-700 font-bold text-lg">레벨{level}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 고2 수준 */}
          <div className="flex flex-col items-center">
            <div className="mb-4">
              <span className="inline-block px-5 py-2 rounded-full text-white font-bold text-base bg-gradient-to-r from-emerald-500 to-green-600">
                고2 수준 듣기
              </span>
            </div>
            <div className="flex gap-4">
              {[13, 14].map((level) => (
                <div key={level} className="flex flex-col items-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl shadow-md flex items-center justify-center border-2 border-emerald-300">
                    <span className="text-emerald-700 font-bold text-lg">레벨{level}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 고3 수준 */}
          <div className="flex flex-col items-center">
            <div className="mb-4">
              <span className="inline-block px-5 py-2 rounded-full text-white font-bold text-base bg-gradient-to-r from-emerald-500 to-green-600">
                고3 수준 듣기
              </span>
            </div>
            <div className="flex gap-4">
              {[15, 16].map((level) => (
                <div key={level} className="flex flex-col items-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-green-100 rounded-xl shadow-md flex items-center justify-center border-2 border-emerald-300">
                    <span className="text-emerald-700 font-bold text-lg">레벨{level}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-950">
      <Navigation currentPage="curriculum" />

      {/* Hero Section */}
      <section className="relative pt-20 pb-10 md:pt-32 md:pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-emerald-400/10 border border-emerald-400/30 rounded-full mb-6">
            <span className="text-emerald-400 text-sm font-semibold">ENGLISH CURRICULUM</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            <span className="md:hidden">미래인재학원<br />영어 커리큘럼</span>
            <span className="hidden md:inline">미래인재학원 영어 커리큘럼</span>
          </h1>
        </div>
      </section>

      {/* Course Type Selection */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center gap-6">
            <div className="flex gap-4">
              <button
                onClick={() => setCourseType('middle')}
                className={`px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer border-2 ${
                  courseType === 'middle'
                    ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg'
                    : 'bg-white text-emerald-500 border-emerald-500 hover:bg-emerald-50'
                }`}
              >
                중등과정
              </button>
              <button
                onClick={() => setCourseType('high')}
                className={`px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer border-2 ${
                  courseType === 'high'
                    ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg'
                    : 'bg-white text-emerald-500 border-emerald-500 hover:bg-emerald-50'
                }`}
              >
                고등과정
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Category Toggle Section - 중등과정 */}
      {courseType === 'middle' && (
        <section className="py-4 sm:py-10 bg-gray-50/95 sticky top-[80px] z-30 backdrop-blur-md border-b border-gray-200 shadow-sm">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center">
              <div className="grid grid-cols-4 gap-2 sm:gap-3 w-full max-w-3xl">
                {middleCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setMiddleCategory(cat.id as MiddleCategoryType)}
                    className={`px-2 sm:px-6 py-3 sm:py-4 rounded-full font-bold text-xs sm:text-base transition-all duration-300 whitespace-nowrap cursor-pointer border-2 ${
                      middleCategory === cat.id
                        ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg'
                        : 'bg-white text-emerald-500 border-emerald-500 hover:bg-emerald-50'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Category Toggle Section - 고등과정 */}
      {courseType === 'high' && (
        <section className="py-4 sm:py-10 bg-gray-50/95 sticky top-[80px] z-30 backdrop-blur-md border-b border-gray-200 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center">
              <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full max-w-md">
                {highCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setHighCategory(cat.id)}
                    className={`px-4 sm:px-10 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all duration-300 whitespace-nowrap cursor-pointer border-2 ${
                      highCategory === cat.id
                        ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg'
                        : 'bg-white text-emerald-500 border-emerald-500 hover:bg-emerald-50'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 중등과정 Content */}
      {courseType === 'middle' && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {middleCategory === 'reading' && renderTextbookSection(readingData)}
            {middleCategory === 'vocabulary' && renderVocabularySection()}
            {middleCategory === 'grammar' && renderTextbookSection(grammarData)}
            {middleCategory === 'listening' && renderListeningSection()}
          </div>
        </section>
      )}

      {/* 고등과정 Content */}
      {courseType === 'high' && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {highCategory === 'regular' && (
              <>
                <div className="text-center mb-16">
                  <div className="text-4xl font-bold text-gray-900 mb-4">
                    고등 영어 <span className="text-emerald-600">내신수업</span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {/* 학교별 수업 */}
                  <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-8 border border-emerald-100 hover:shadow-xl transition-all">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-school-line text-3xl text-white"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">학교별 수업</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      영어 수업은 학교별로 진도가 다르기 때문에, 학교별 수업이 필수입니다.
                    </p>
                  </div>

                  {/* 철저한 수행관리 */}
                  <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-8 border border-emerald-100 hover:shadow-xl transition-all">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-list-3-line text-3xl text-white"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">철저한 수행관리</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      학교 성적의 40% 이상은 수행평가로 인해서 결정됩니다. 철저한 수행평가 관리가 필수입니다.
                    </p>
                  </div>

                  {/* 4주간 시험 준비 */}
                  <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-8 border border-emerald-100 hover:shadow-xl transition-all">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-check-line text-3xl text-white"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">4주간 시험 준비</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      시험 준비로 4주 동안은 주3회 수업에서 주1회 수업이 추가되서, 주4회 수업이 진행됩니다.
                    </p>
                  </div>

                  {/* 동형 모의고사 */}
                  <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-8 border border-emerald-100 hover:shadow-xl transition-all">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-copy-2-line text-3xl text-white"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">동형 모의고사</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      학교만을 위한 동형 모의고사를 제작해 제공합니다.
                    </p>
                  </div>

                  {/* 매주 주간 고사 */}
                  <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-8 border border-emerald-100 hover:shadow-xl transition-all">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-text-line text-3xl text-white"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">4주간 주간 고사</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      내신 시험 대비를 위해서, 매주 내신 주간 고사를 진행합니다. 학생들의 부족한 점을 찾아서 보완합니다.
                    </p>
                  </div>

                  {/* 무한 영상 보강 */}
                  <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-8 border border-emerald-100 hover:shadow-xl transition-all">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-video-line text-3xl text-white"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">무한 영상 보강</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      모든 수업을 실시간으로 촬영하고 있습니다. 학생 결석시 선생님의 지도하에 보충 영상 수업을 진행합니다.
                    </p>
                  </div>
                </div>

                {/* 고등 영어 내신 교재 섹션 */}
                <div className="mt-20">
                  <div className="text-center mb-12">
                    <h3 className="text-4xl font-bold text-gray-900">
                      고등 영어 <span className="text-emerald-600">내신 교재</span>
                    </h3>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                    {/* 기본서 */}
                    <div className="bg-white rounded-2xl p-4 sm:p-8 border border-gray-200 shadow-lg">
                      <div className="text-center mb-8">
                        <span className="inline-block px-6 py-3 bg-emerald-600 text-white font-bold rounded-full text-lg">
                          기본서
                        </span>
                      </div>
                      <div className="flex justify-center gap-2 sm:gap-4">
                        <div className="flex flex-col items-center">
                          <div className="w-32 h-44 sm:w-44 sm:h-60 bg-gray-100 rounded-xl overflow-hidden shadow-md border-2 border-gray-200 hover:border-emerald-400 transition-all">
                            <img
                              src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/798fa742f4a5d4df2caa35d3478471bf.jpeg"
                              alt="기본서 1"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="mt-3 text-xs sm:text-sm text-gray-700 font-medium text-center">내신 진도 자체 교재</p>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className="w-32 h-44 sm:w-44 sm:h-60 bg-gray-100 rounded-xl overflow-hidden shadow-md border-2 border-gray-200 hover:border-emerald-400 transition-all">
                            <img
                              src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/971cf88c8050c7089295203dd1c86632.jpeg"
                              alt="기본서 2"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <p className="mt-3 text-xs sm:text-sm text-gray-700 font-medium text-center">내신 워크북 교재</p>
                        </div>
                      </div>
                    </div>

                    {/* 시험기간 보충교재 */}
                    <div className="bg-white rounded-2xl p-4 sm:p-8 border border-gray-200 shadow-lg">
                      <div className="text-center mb-8">
                        <span className="inline-block px-6 py-3 bg-emerald-600 text-white font-bold rounded-full text-lg">
                          시험기간 보충교재
                        </span>
                      </div>
                      <div className="flex justify-center gap-1 sm:gap-3">
                        {[
                          { num: 1, img: 'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/c92906aeb12b2843209d8d16c30b2a87.jpeg', label: '전기가 찌릿 찌릿(전국 기출 문제 모음집)' },
                          { num: 2, img: 'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/996d7bd80aebb4341809be5f7a288ccd.jpeg', label: '너,다마자(너에게 다주는 마지막 자료)' },
                          { num: 3, img: 'https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/64a987ba364e6eda71a736e7632dcec3.jpeg', label: '동형모의고사' }
                        ].map((item) => (
                          <div key={item.num} className="flex flex-col items-center">
                            <div className="w-24 h-32 sm:w-36 sm:h-48 bg-gray-100 rounded-xl overflow-hidden shadow-md border-2 border-gray-200 hover:border-emerald-400 transition-all flex items-center justify-center">
                              {item.img ? (
                                <img
                                  src={item.img}
                                  alt={`시험기간 보충교재 ${item.num}`}
                                  className="w-full h-full object-cover object-top"
                                />
                              ) : (
                                <div className="flex flex-col items-center justify-center text-gray-400">
                                  <i className="ri-image-add-line text-2xl mb-1"></i>
                                  <span className="text-xs font-medium">이미지</span>
                                </div>
                              )}
                            </div>
                            <p className="mt-3 text-xs sm:text-sm text-gray-700 font-medium text-center max-w-[80px] sm:max-w-none">{item.label}</p>
                          </div>
                        ))}
                      </div>
                      <p className="text-center text-gray-600 mt-6 text-sm">
                        위의 교재는 시험기간에만 배포하고 있습니다
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {highCategory === 'suneung' && (
              <>
                <div className="text-center mb-16">
                  <div className="text-4xl font-bold text-gray-900 mb-4">
                    고등 영어 <span className="text-emerald-600">수능수업</span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 mb-20">
                  {/* 환급특강 */}
                  <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-8 border border-emerald-100 hover:shadow-xl transition-all">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-medal-line text-3xl text-yellow-400"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">환급특강</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      수능 최저 등급 확보를 강력히 장려하기 위해 수능 1등급 달성 시, 3학년 2학기 영어 수강료의 50%를 환급해 드립니다. 미래인재학원은 학생의 노력이 실질적인 보상과 최고의 결과로 이어지도록 끝까지 함께합니다.
                    </p>
                  </div>

                  {/* 매주 주간 모의고사 */}
                  <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-8 border border-emerald-100 hover:shadow-xl transition-all">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-edit-line text-3xl text-white"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">매주 주간 모의고사</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      방학 기간에는 매주 모의고사 시험을 진행하고 있어, 학생들이 수능에 대한 감이 잃지 않도록 진행하고 있습니다.
                    </p>
                  </div>

                  {/* 방학 특강 */}
                  <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-8 border border-emerald-100 hover:shadow-xl transition-all">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-sun-line text-3xl text-white"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">방학 특강</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      수능 최저는 고3 때 시작하면 늦습니다. 미래인재학원은 매 방학마다 '킬러문항특강'을 개설하여, 고1·2 시기부터 내신과 수능의 균형을 잡도록 장려합니다. 학기 중에는 내신에 집중하고, 방학에는 수능 감각을 끌어올리는 전략으로 수능 최저를 맞추기 위해서 노력합니다.
                    </p>
                  </div>

                  {/* EBS 활용 */}
                  <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-8 border border-emerald-100 hover:shadow-xl transition-all">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-book-open-line text-3xl text-white"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">EBS 활용</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      수능 영어에서 EBS는 높은 연계율을 보입니다. 그렇기 때문에 고3 뿐만 아니라, 고1,고2 학기 중에도 EBS 수능특강, 수능완성을 활용하고 있습니다.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">영어 실력 향상의 시작</h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            <span className="md:hidden">체계적인 커리큘럼과 전문 강사진이<br />함께합니다</span>
            <span className="hidden md:inline">체계적인 커리큘럼과 전문 강사진이 함께합니다</span>
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-10 py-4 bg-amber-400 text-black text-lg font-bold rounded-full hover:bg-amber-300 transition-all shadow-xl shadow-amber-400/20 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-check-line text-xl"></i>
            입학상담
          </Link>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
